import axios, { CancelTokenSource } from 'axios';
import _ from 'lodash';
import { IS_BROWSER } from '@/common/constant/index';
import EventEmitter from '@/common/EventEmitter';
import { TaskStatus } from '@/renderer/scripts/utils/TaskManager/types';
import { DownloadFileInfo } from '@/renderer/scripts/utils/download/types';
import { calcRemainingTime, initId } from '@/renderer/scripts/utils/tools';
import { downloadStreamManager, uploadStreamManager } from '@/renderer/scripts/utils/StreamManager';
import { StreamGroup } from './StreamManager/lib/StreamGroup';

export const LoaderConfig = {
  // 文件并发数
  ChunkParallelLimit: IS_BROWSER ? 2 : 5,
};
// 默认上传配置
export enum UploadConfig {
  // 文件并发数
  ChunkParallelLimit = LoaderConfig.ChunkParallelLimit,
  // 分块并发数
  ChunkRetryTimes = 5,
  // 分块重试次数
  ChunkSize = 5 * 1024 * 1024,
  // 分块大小
  ProgressInterval = 1000,
  // 进度事件发送间隔
  MaxPartNumber = 10000,
  // 分片数量最大值
  PartUploadFileSize = 32 * 1024 * 1024,
  // 超过多大的文件则使用分片上传
  COSRequestMaxBodyLength = 5 * 1024 * 1024 * 1024,
  // 当前 COS 请求接口最大传输 5GB
  QuickUploadBeginSize = 1 * 1024 * 1024,
  // 秒传首次检查hash的大小
  MaxGroupPartRange = 100,
  // 进行分组的分块数最大长度(私有化专用)
  StreamHighWaterMark = 1 * 1024 * 1024,
} // 设置流每次最高读取大小

/**
 * 【非常重要】PartDownloadFileSize需要固定大于等于20
 * 图片加水印后下载到本地，size和crc64会和云上文件不同，导致校验失败
 * PartDownloadFileSize规定了20MB以上文件才走分块
 * 万象水印要求图片大小小于20MB，刚好绕开了这个坑
 */
export enum DownloadConfig {
  // 文件并发数
  FileParallelLimit = LoaderConfig.ChunkParallelLimit,
  // 分块并发数
  ChunkParallelLimit = 5,
  // 分块重试次数
  ChunkRetryTimes = 5,
  // 分块大小
  ChunkSize = 5 * 1024 * 1024,
  // 进度事件发送间隔
  ProgressInterval = 1000,
  // 分片数量最大值
  MaxPartNumber = 10000,
  // 超过多大的文件则使用分片下载
  PartDownloadFileSize = 32 * 1024 * 1024,
  // 当前 COS 请求接口最大传输 5 GB
  COSRequestMaxBodyLength = 5 * 1024 * 1024 * 1024,
  // 写数据超时时间
  MaxWriteDataTimeout = 1000 * 60,
  // 请求超时设置时间长一些，避免出现限速比较慢，上传比较久的情况
  RequestTimeout = 1000 * 60 * 5,
}
export interface TaskExecutor extends EventEmitter {
  start(restart?: boolean, collectToken?: string): Promise<void>;
  pause(): void;
  isPaused(): boolean;
  isRunning(): boolean;
  cancel(changeState?: boolean, isLogined?: boolean): void;
  isFinished(): boolean;
  updateChunkParallelLimit(v: number): void;
  updateChunkRetryTimes(v: number): void;
}
export class CommonLoader<T extends Uploader.UploadFileInfo | DownloadFileInfo> extends EventEmitter {
  // 默认
  protected chunkParallelLimit: number = IS_BROWSER ? 2 : 5; // 分片个数

  protected readonly id: string;

  // 当前Loader的类型
  protected readonly type: 'upload' | 'download';
  // 取消请求使用
  protected cancelRequestSource: CancelTokenSource = axios.CancelToken.source();

  // 文件信息
  protected info: T;
  // 分片重试次数
  protected chunkRetryTimes = 5;

  // 当前状态
  protected status: TaskStatus = TaskStatus.waiting;

  // 当前Loader的streamGroup引用
  protected streamGroup: StreamGroup | null;

  /**
   * @description 触发进度事件
   * @private
   * @memberof Uploader
   */
  protected emitProgress;

  /**
   * @description 触发hash进度事件
   * @private
   * @memberof Uploader
   */
  protected emitHashProgress;

  constructor(type: 'upload' | 'download', info: T) {
    super();
    this.type = type;
    this.info = info;
    this.id = initId();
    this.emitProgress = this.getProgress('progress', type);
    this.emitHashProgress = this.getProgress('hashProgress', type);
    if (!IS_BROWSER) {
      if (type === 'upload') {
        this.streamGroup = uploadStreamManager.createGroup(this.id, this.info.name);
      } else {
        this.streamGroup = downloadStreamManager.createGroup(this.id, this.info.name);
      }
    } else {
      this.streamGroup = null;
    }
  }

  // 任务是否运行中
  isRunning(): boolean {
    return (
      this.status === TaskStatus.running ||
      this.status === TaskStatus.waiting ||
      this.status === TaskStatus.checking ||
      this.status === TaskStatus.preparing ||
      this.status === TaskStatus.ready
    );
  }
  // 任务是否结束
  isFinished(): boolean {
    return [TaskStatus.success, TaskStatus.error, TaskStatus.canceled].includes(this.status);
  }
  pause(): void {
    this.isRunning() && this.setStatus(TaskStatus.paused);
  }

  cancel(notChangeState?: boolean, isLogined?: boolean): void {
    !notChangeState && this.setStatus(TaskStatus.canceled);
  }
  cancelRequest() {
    this.cancelRequestSource.cancel('canceled');
    this.cancelRequestSource = axios.CancelToken.source();
  }

  /**
   * 任务success/error/finish/cancel/pause的时候，需要从Manager中关闭流，且取消相关引用
   * @description
   * 取消相关引用的目的是为了方便StreamManager的速度计算
   * @protected
   */
  private _cancelStreamGroup() {
    if (IS_BROWSER) return;
    if (this.type === 'upload') {
      uploadStreamManager.deleteGroupByLoaderId(this.id);
    }
    if (this.type === 'download') {
      downloadStreamManager.deleteGroupByLoaderId(this.id);
    }
  }
  // 更新分片个数(PC)
  public updateChunkParallelLimit(v: number) {
    if (IS_BROWSER) return;
    this.chunkParallelLimit = v;
  }
  public updateChunkRetryTimes(v: number) {
    this.chunkRetryTimes = v;
  }

  protected getStatus(): TaskStatus {
    return this.status;
  }

  setStatus(value: TaskStatus): void {
    const oldValue = this.status; // 先复制一个引用
    this.status = value;
    if (value === TaskStatus.canceled || value === TaskStatus.error || value === TaskStatus.success) {
      if (!IS_BROWSER) {
        this._cancelStreamGroup();
      }
    }
    // 暂停则
    if (value === TaskStatus.paused) {
      if (this.streamGroup) {
        this.streamGroup.destroyAllStreams();
      }
    }
    if (oldValue !== this.status) {
      this.emit('status_changed', value);
    }
  }

  isPaused(): boolean {
    return this.status === TaskStatus.paused;
  }

  private getProgress(progressEventName: string, type: 'upload' | 'download') {
    const total = this.info.size;
    // size1 新数据 , size0 旧数据
    let [size0, size1] = [0, 0];
    let [time0, time1] = [Date.now(), Date.now()];
    let timer = 0;
    let speedList: number[] = [];
    // 超时将速度重置为0
    const checkNoData = _.debounce(() => {
      update();
    }, UploadConfig.ProgressInterval * 5);
    // 获取上传的配置间隔
    const progressInterval = this.type === 'upload' ? UploadConfig.ProgressInterval : DownloadConfig.ProgressInterval;
    /**
     * 更新进度条
     * @description
     * 依赖了 size1,size0,
     */
    const update = () => {
      timer = 0;
      time1 = Date.now();
      const speed = Math.max(0, Math.round(((size1 - size0) / ((time1 - time0) / 1000)) * 100) / 100) || 0;
      if (speed === 0) {
        speedList = [];
      }
      speedList.push(speed);
      if (speedList.length > 3) {
        speedList.shift();
      }
      const totalSpeed = speedList.reduce((prev: number, curr: number) => prev + curr, 0);
      // 平均速度
      const averageSpeed = Number((totalSpeed / speedList.length).toFixed(2));
      let percent;
      if (size1 === 0 && total === 0) {
        percent = 1;
      } else {
        percent = Math.floor((size1 / total) * 100) / 100 || 0;
      }
      time0 = time1;
      size0 = size1;
      const { remainingMessage, remainingTime } = calcRemainingTime(averageSpeed, total, percent);
      this.emit(progressEventName, {
        loaded: size1,
        total,
        speed: averageSpeed,
        remainingMessage,
        remainingTime,
        percent: +percent.toFixed(2),
      });
    };
    /**
     *
     * @desc emitProgress
     * @param loaded 加载的总量
     * @param immediately 是否立刻开始
     * @param init 是否初始化数据
     * @param startSize 数据从什么时候开始（需要ini=true)
     */
    return function emitProgress(
      loaded: number,
      options?: { init?: boolean; immediately?: boolean; startSize?: number },
    ) {
      const needInit = options?.init === true;
      const startSize = options?.startSize;
      if (needInit && startSize !== undefined) {
        size0 = startSize;
        size1 = startSize;
        return;
      }
      if (loaded) {
        size1 = loaded;
      }
      if (options?.immediately) {
        window.clearTimeout(timer);
        update();
      } else {
        if (timer) return;
        timer = window.setTimeout(update, progressInterval);
        checkNoData();
      }
    };
  }
}
